import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { FlowV2_Action_Contact_AssignFragment } from '~/graphql/types';
import useOfficeOptions from '~/hooks/useOfficeOptions';
import useUserOptions from '~/hooks/useUserOptions';
import Dropdown, { SelectedOptionOf } from '~/components/molecule/Dropdown';
import { Props as FormProps } from '../ActionForm';
import TEST_ID from './index.testid';
import useEntityValidationForInputs from '../../../Builder/hooks/useEntityValidationForInputs';
import FieldLabel from '../../../FieldLabel';
import Checkbox from '~/components/molecule/Checkbox';
import ToggleCheckbox from '~/components/molecule/ToggleCheckbox';
import TipBanner from '~/components/organism/TipBanner';
import Link from '~/components/molecule/Link';
import JustificationContainer from '~/components/atom/JustificationContainer';
import useBuilderContext from '../../../Builder/hooks/useBuilderContext';
import useRelativeMaps from '../../../Builder/hooks/useRelativeMaps';
import Selector from '../Selector';
import PositionWrapper from '../ConditionEditorV2/components/PositionWrapperV2';
import EditableBlockWithIcon from '~/components/atom/EditableBlockWithIcon';
import ActionLabelAsText from '../../../Builder/components/ActionLabelAsText';
import isPointerArgument from '../../../../util/isPointerArgument';
import getPathForPointer from '../Selector/utils/getPathForPointer';
import {
  getPathRepresentation,
  text as labelText,
} from '../ConditionEditorV2/utils/getLabelForRep';
import type { FlowPath } from '../Selector/utils/getFieldsByPath';
import { FlowAction } from '~/graphql/types';

export type Props = FormProps & {
  dataTestId?: string;
  action: FlowV2_Action_Contact_AssignFragment;
};

const text = {
  userLabel: 'Selecteer gebruiker',
  officeLabel: 'Selecteer vestiging',
  checkboxName: 'overwriteAssignee',
  checkboxLabel: 'Huidige eigenaar van het contact overschrijven',
  officeDropdownLabel: 'Selecteer vestiging',
  userDropdownLabel: 'Selecteer gebruiker',
  assignToWorkingArea: 'Toewijzen op basis van mijn werkgebieden',
  addressLabel: 'Adres',
  addressEmptyValue: 'Selecteer een adres',
  whichAddress: 'Gebruik adres uit:',
  defaultContactAddress: 'Adres van het contact',
  selectVariable: 'Kies variabele',
  useDefaultAddress: 'Gebruik contactadres',

  workingAreaTipHeader:
    'Hoe verloopt toewijzen buiten mijn werkgebieden of als er geen adres gevonden kan worden?',
  workingAreaTipText: (
    <>
      Geef hieronder aan welke vestiging en gebruiker de contacten toegewezen
      krijgen die buiten de werkgebieden van je vestiging(en) vallen.
      Werkgebieden kan je vastleggen in de vestigingsinstellingen.
      <br />
      <JustificationContainer margin={['xxs', null, null, null]}>
        <Link to="/-/settings/offices" target="_blank">
          Werkgebieden instellen in vestigingen
        </Link>
      </JustificationContainer>
    </>
  ),
};

const AssignContact: React.FCC<Props> = ({ action, onChange }) => {
  const [showAddressSelector, setShowAddressSelector] = useState(false);
  const addressSelectorRef = useRef<null | HTMLDivElement>(null);
  const maps = useRelativeMaps({ actionId: action.id });
  const { opts, primitiveInput, primitiveListInput } = useBuilderContext();

  const { office, user, overwriteAssignee, setByWorkingArea, address } = action;

  // Derive current address pointer path/label for display
  let addressSelectorPath: FlowPath = [];
  let addressVariableName: string | null = null;
  let addressHasError = false;
  if (address && isPointerArgument(address)) {
    const path = getPathForPointer(address.pointer, {
      subjectMap: maps.subjectMap,
      instanceMap: maps.instanceMap,
      directoryMap: maps.directoryMap,
    });
    if (path.error === undefined) {
      addressSelectorPath = path.result;
      addressVariableName = getPathRepresentation(path.fullResult);
    } else {
      // Pointer no longer resolves – mark as error and show a fallback label
      addressHasError = true;
      addressVariableName = labelText.invalidVariable;
    }
  }

  const officeOptions = useOfficeOptions();

  const userOptions = useUserOptions({
    withAllUsersOption: true,
    officeId: action.office,
  });

  const onCheckboxClick = () => {
    onChange({
      ...action,
      overwriteAssignee: !action.overwriteAssignee,
    });
  };

  const onChooseOffice = (option: SelectedOptionOf<any>) => {
    onChange({
      ...action,
      office: option.option.payload?.id,
    });
  };

  const onChooseUser = (option: SelectedOptionOf<any>) => {
    onChange({
      ...action,
      user: option.option.payload?.id,
    });
  };

  const onSetByWorkingAreaChange = () => {
    const newAddress = !setByWorkingArea
      ? {
          __typename: 'Flow___Argument_Pointer' as const,
          pointer: {
            __typename: 'Flow___Pointer' as const,
            path: ['global', 'contact', 'address'] as Array<string>,
          },
        }
      : undefined;

    onChange({
      ...action,
      setByWorkingArea: !setByWorkingArea,
      address: newAddress,
    });
  };

  const { userError, officeError } = useEntityValidationForInputs({
    userOptions,
    userId: action.user,
    officeId: action.office,
  });

  return (
    <>
      <ToggleCheckbox
        containerProps={{
          align: 'center',
          width: '100%',
          justification: 'space-between',
          margin: ['base', null],
        }}
        label={text.assignToWorkingArea}
        value={setByWorkingArea ?? false}
        onChange={() => {
          onSetByWorkingAreaChange();
        }}
      />
      {setByWorkingArea && (
        <>
          <JustificationContainer margin={[null, null, 'xl', null]}>
            <JustificationContainer
              direction="column"
              width="100%"
              align="start"
              gap="xs"
            >
              <FieldLabel>{text.whichAddress}</FieldLabel>
              <JustificationContainer gap="xxs" align="baseline">
                <EditableBlockWithIcon
                  onClick={e => {
                    e.stopPropagation();
                    setShowAddressSelector(true);
                  }}
                  icon="map-pin"
                  active={false}
                  hasError={addressHasError}
                  ref={addressSelectorRef}
                >
                  {addressVariableName ? (
                    <ActionLabelAsText str={addressVariableName} />
                  ) : (
                    text.defaultContactAddress
                  )}
                </EditableBlockWithIcon>
              </JustificationContainer>
            </JustificationContainer>

            {showAddressSelector && (
              <PositionWrapper
                onClose={() => setShowAddressSelector(false)}
                parentRef={addressSelectorRef.current}
              >
                <Selector
                  onClose={() => setShowAddressSelector(false)}
                  onSelect={value => {
                    if (
                      value.__typename === 'Flow___Argument_File' ||
                      value.__typename === 'Flow___InstanceCondition' ||
                      value.__typename === 'Flow___SubjectFieldCondition'
                    ) {
                      return;
                    }

                    onChange({
                      ...action,
                      address: value,
                    });
                    setShowAddressSelector(false);
                  }}
                  inputPrimitives={primitiveInput}
                  inputListPrimitives={primitiveListInput}
                  argument={undefined}
                  initialPath={addressSelectorPath}
                  opts={{
                    ...opts,
                    ...maps,
                    action: FlowAction.ContactAssign,
                    conditionType: 'condition',
                    limitToInstanceSubjects: [
                      {
                        type: 'FlowSubject_Primitive_Address',
                        typeId: null,
                      },
                    ],
                  }}
                />
              </PositionWrapper>
            )}
          </JustificationContainer>
          <TipBanner
            id="working_area_tip"
            headerText={text.workingAreaTipHeader}
            dismissible={false}
          >
            {text.workingAreaTipText}
          </TipBanner>
        </>
      )}

      <JustificationContainer
        width="100%"
        direction="column"
        gap="base"
        margin={[null, null, 'base', null]}
      >
        <JustificationContainer width="100%" align="center">
          <FieldLabel>{text.officeLabel}</FieldLabel>
          <Dropdown
            dataTestId={TEST_ID.OFFICE_DROPDOWN}
            label={text.officeDropdownLabel}
            options={officeOptions}
            onChange={onChooseOffice}
            selectedOptionIdx={officeOptions.findIndex(
              selectedOffice => selectedOffice.payload?.id === office,
            )}
            error={officeError}
          />
        </JustificationContainer>
        <JustificationContainer width="100%" align="center">
          <FieldLabel>{text.userLabel}</FieldLabel>
          <Dropdown
            dataTestId={TEST_ID.USER_DROPDOWN}
            label={text.userDropdownLabel}
            options={userOptions}
            onChange={onChooseUser}
            selectedOptionIdx={
              userOptions.findIndex(selectedUser =>
                user === null
                  ? selectedUser.payload === user
                  : selectedUser.payload?.id === user,
              ) ?? null
            }
            error={userError}
          />
        </JustificationContainer>
      </JustificationContainer>
      <StyledCheckbox
        dataTestId={TEST_ID.OVERWRITE_ASSIGNEE}
        value={overwriteAssignee}
        onChange={onCheckboxClick}
        label={text.checkboxLabel}
        name={text.checkboxName}
      />
    </>
  );
};

const StyledCheckbox = styled(Checkbox)<{}>(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    margin-top: ${theme.space('base')};
  `,
);

export default AssignContact;
